/**
 * RTWO Event Bus — Adapter Registries
 */

import type {
  EngineSourceAdapterStatus,
  IEngineSourceAdapter,
  IEngineSourceAdapterRegistry,
} from '../adapters/engine-source-adapter-interface';
import { NullEngineSourceAdapter } from '../adapters/null-engine-source-adapter';

export class EngineSourceAdapterRegistry implements IEngineSourceAdapterRegistry {
  private activeAdapter: IEngineSourceAdapter = new NullEngineSourceAdapter();
  private readonly adapters = new Map<string, IEngineSourceAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IEngineSourceAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
  }

  registerFederationComposite(adapter: IEngineSourceAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    this.activeAdapter = adapter;
  }

  getAllAdapters(): IEngineSourceAdapter[] {
    return Array.from(this.adapters.values());
  }

  isFederationConfigured(): boolean {
    return this.adapters.size > 1 || this.activeAdapter.adapterId.includes('federation');
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-engine-source-adapter') {
      return false;
    }
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullEngineSourceAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IEngineSourceAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return (
      this.activeAdapter.getStatus() !== 'not_configured' ||
      this.activeAdapter.adapterId === 'federation-composite-engine-source-adapter'
    );
  }

  getStatus(): EngineSourceAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export const engineSourceAdapterRegistry = new EngineSourceAdapterRegistry();
